import cn from 'classnames';
import React, { Suspense } from 'react';
import { lazily } from 'react-lazily';
import { Text } from 'src/components/text/Text';
import { type Country } from 'src/types/country';

import styles from './CreditCards.css';

const { ReactComponent: IconAmericanExpress } = lazily(
  () => import('./assets/american-express.svg')
);
const { ReactComponent: IconApplePayBackground } = lazily(
  () => import('./assets/apple-pay-background.svg')
);
const { ReactComponent: IconKnet } = lazily(() => import('./assets/knet.svg'));
const { ReactComponent: IconMada } = lazily(() => import('./assets/mada.svg'));
const { ReactComponent: IconMastercard } = lazily(
  () => import('./assets/master-card.svg')
);
const { ReactComponent: IconVisa } = lazily(() => import('./assets/visa.svg'));
const { ReactComponent: GooglePay } = lazily(
  () => import('./assets/google-pay.svg')
);

const APPLE_PAY_COUNTRIES_LIST = ['UAE', 'KSA', 'KWT', 'QAT'];

interface Props {
  className?: string;
  country: Country;
  productType?: 'installments';
  title?: string;
  fullWidth?: boolean;
}

export const CreditCards = ({
  title,
  className,
  country,
  fullWidth,
}: Props) => {
  return (
    <Suspense fallback={null}>
      <div
        className={cn(
          styles.CreditCards,
          { [styles.CreditCards_fullWidth]: fullWidth },
          className
        )}
      >
        {!!title && (
          <Text
            as="p"
            variant="caption"
            className={styles.CreditCards__Title}
            data-test="alshaya-popup-payment-methods-title"
          >
            {title}
          </Text>
        )}

        <div className={styles.CreditCards__Cards}>
          <div className={styles.CreditCards__Card} data-test="mastercard-icon">
            <IconMastercard />
          </div>
          <div className={styles.CreditCards__Card} data-test="visa-icon">
            <IconVisa />
          </div>
          {country === 'KWT' && (
            <div className={styles.CreditCards__Card} data-test="knet-icon">
              <IconKnet />
            </div>
          )}
          {country === 'KSA' && (
            <>
              <div className={styles.CreditCards__Card} data-test="mada-icon">
                <IconMada />
              </div>

              <div className={styles.CreditCards__Card} data-test="amex-icon">
                <IconAmericanExpress />
              </div>
            </>
          )}
          {APPLE_PAY_COUNTRIES_LIST.includes(country) && (
            <div className={styles.CreditCards__Card} data-test="applepay-icon">
              <IconApplePayBackground />
            </div>
          )}
          {country === 'UAE' && (
            <div
              className={styles.CreditCards__Card}
              data-test="google-pay-icon"
            >
              <GooglePay />
            </div>
          )}
        </div>
      </div>
    </Suspense>
  );
};
